import { z } from 'zod';

import { IsoDateTimeSchema, NullableIsoDateTimeSchema } from '../common/datetime';
import { AdminId, RebindRequestId, WorkerId } from '../common/ids';
import { CursorQuerySchema, pageSchema } from '../common/pagination';
import { DevicePlatform, DisplayMode } from '../staff/devices';

import { ReasonSchema, SiteRefSchema } from './workers';

// ---------------------------------------------------------------------------
// Enums (data-model.md · device_rebind_requests)
// ---------------------------------------------------------------------------

export const RebindStatus = z.enum(['PENDING', 'APPROVED', 'REJECTED', 'EXPIRED']);
export type RebindStatus = z.infer<typeof RebindStatus>;

// ---------------------------------------------------------------------------
// Entidad (contracts/admin-workers.md · RebindRequest)
// ---------------------------------------------------------------------------

export const RebindRequestSchema = z.object({
  id: RebindRequestId,
  worker: z.object({
    id: WorkerId,
    first_names: z.string(),
    last_names: z.string(),
    primary_site: SiteRefSchema,
  }),
  platform: DevicePlatform,
  display_mode: DisplayMode,
  status: RebindStatus,
  requested_at: IsoDateTimeSchema,
  expires_at: IsoDateTimeSchema,
  resolved_at: NullableIsoDateTimeSchema,
  /** Admin que aprobó/rechazó; `null` mientras está `PENDING` o si venció sola (`EXPIRED`). */
  resolved_by: z
    .object({
      id: AdminId,
      name: z.string(),
    })
    .nullable(),
});
export type RebindRequest = z.infer<typeof RebindRequestSchema>;

// ---------------------------------------------------------------------------
// Requests
// ---------------------------------------------------------------------------

/** `GET /rebind-requests?status=PENDING&cursor&limit` (solo scope). */
export const RebindListQuerySchema = CursorQuerySchema.extend({
  status: RebindStatus.optional(),
});
export type RebindListQuery = z.infer<typeof RebindListQuerySchema>;

/** Parámetro de ruta `{id}` de solicitud de re-vinculación. */
export const RebindIdParamsSchema = z.object({ id: RebindRequestId });
export type RebindIdParams = z.infer<typeof RebindIdParamsSchema>;

/** `POST /rebind-requests/{id}/reject`: `{ reason? }`. */
export const RejectRebindBodySchema = z.object({
  reason: ReasonSchema.optional(),
});
export type RejectRebindBody = z.infer<typeof RejectRebindBodySchema>;

// ---------------------------------------------------------------------------
// Responses
// ---------------------------------------------------------------------------

/** `200 Page<RebindRequest>` */
export const RebindListResponseSchema = pageSchema(RebindRequestSchema);
export type RebindListResponse = z.infer<typeof RebindListResponseSchema>;
